import { ChatbotMessage } from '@/components/molecules/ChatbotMessage/ChatbotMessage'
import { ChatbotToolPill } from '@/components/molecules/ChatbotToolPill/ChatbotToolPill'
import { MessageAttachments } from './MessageAttachments'
import type { ChatbotContext, ChatMessage, ToolPillState } from '@/machines/chatbotMachine'
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type TouchEvent,
  type WheelEvent,
} from 'react'

export interface AssistantMessageListProps {
  messages: ChatMessage[]
  streaming: boolean
  sending: boolean
  currentToolCalls: ToolPillState[]
  currentResponse: string
  error: ChatbotContext['error']
  /** Anonymous (public-dashboard) mode — hides per-message usage/cost footers. */
  anon: boolean
  userBubbleStyle?: CSSProperties
  assistantBubbleStyle?: CSSProperties
}

export interface AssistantMessageListHandle {
  /** Re-arms stick-to-bottom following. Call this on an explicit "I'm back in
   *  the live conversation" action (e.g. the caller is about to send a new
   *  message) so the new turn is visible even if the user had scrolled up. */
  resumeFollowing: () => void
}

/**
 * Scrollable message list — messages, streaming bubble + tool pills, error
 * banner, and the WebKit-safe stick-to-bottom scroll machinery. Pure/props-driven
 * — no context reads, no machine access.
 *
 * Memoized: every message is re-parsed by ReactMarkdown on each render, and a
 * parent can re-render many times a second for reasons that never touch the
 * transcript — the sitewide assistant's window re-renders on every pointermove
 * of a header drag, and on every window resize. Its props are the machine's
 * context values, replaced (never mutated) on change, so a shallow compare is
 * exact.
 */
export const AssistantMessageList = memo(
  forwardRef<AssistantMessageListHandle, AssistantMessageListProps>(function AssistantMessageList(
    {
      messages,
      streaming,
      sending,
      currentToolCalls,
      currentResponse,
      error,
      anon,
      userBubbleStyle,
      assistantBubbleStyle,
    },
    ref,
  ) {
    const scrollRef = useRef<HTMLDivElement>(null)

    // Auto-follow the streaming reply to the bottom — cross-browser.
    //
    // The old "jumps to the FIRST message while streaming" bug was a WebKit/Safari behaviour
    // (verified in the WebKit engine): when a message's DOM subtree is replaced — ReactMarkdown
    // re-renders the whole message on each token — WebKit RESETS scrollTop to 0. Chrome keeps
    // it. `overflow-anchor` does not exist in Safari, so the earlier CSS fix was a no-op there.
    //
    // The fix: a MutationObserver that re-pins to the bottom after EVERY DOM change while
    // pinned. It fires synchronously (before paint), so it undoes WebKit's reset with no visible
    // flicker, and it doesn't depend on any CSS. Crucially, the follow is released ONLY on real
    // USER input (wheel / touch-drag / keyboard) — never on a browser-initiated scroll change.
    // Reading the scroll POSITION to decide releases is what made it stick, because WebKit's
    // reset looks exactly like a huge user scroll-up. Position is used ONLY to re-arm at the
    // very bottom (unambiguous). Re-arms also on send (onSubmit) and the ↓ button.
    const stickToBottom = useRef(true)
    const [showJump, setShowJump] = useState(false)

    const setPinned = (pinned: boolean) => {
      stickToBottom.current = pinned
      setShowJump(!pinned)
    }

    const pinToBottom = () => {
      const el = scrollRef.current
      if (el) el.scrollTop = el.scrollHeight
    }

    useEffect(() => {
      const el = scrollRef.current
      if (!el) return
      const follow = () => {
        if (stickToBottom.current) el.scrollTop = el.scrollHeight
      }

      // A narrower dock or window reflows the transcript TALLER without a single DOM change, and so
      // does an image finishing loading: the MutationObserver never fires and a pinned list drifts
      // up off the bottom. A ResizeObserver on the container and on each of its rows re-pins then.
      const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(follow)
      ro?.observe(el)
      for (const row of Array.from(el.children)) ro?.observe(row)

      follow()
      const mo = new MutationObserver(records => {
        for (const record of records) {
          if (!ro || record.target !== el) continue
          record.addedNodes.forEach(node => node instanceof Element && ro.observe(node))
          record.removedNodes.forEach(node => node instanceof Element && ro.unobserve(node))
        }

        follow()
      })
      mo.observe(el, { childList: true, subtree: true, characterData: true })

      return () => {
        mo.disconnect()
        ro?.disconnect()
      }
    }, [])

    // Release the follow only on genuine user intent to scroll up — immune to WebKit's
    // involuntary scrollTop resets (which fire no wheel/touch/key event).
    const release = () => setPinned(false)

    const handleWheel = (e: WheelEvent<HTMLDivElement>) => {
      if (e.deltaY < 0) release()
    }

    const touchY = useRef(0)

    const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
      touchY.current = e.touches[0]?.clientY ?? 0
    }

    const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
      const y = e.touches[0]?.clientY ?? 0
      if (y > touchY.current + 4) release() // finger dragged down → content scrolls up
      touchY.current = y
    }

    // Position is used ONLY to re-arm at the very bottom — never to release.
    const handleScroll = () => {
      const el = scrollRef.current
      if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 12) setPinned(true)
    }

    const jumpToLatest = () => {
      setPinned(true)
      pinToBottom()
    }

    useImperativeHandle(ref, () => ({
      resumeFollowing: () => setPinned(true),
    }))

    return (
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          className="flex-1 space-y-[0.5em] overflow-auto p-[0.75em] [overflow-anchor:none]"
        >
          {messages.length === 0 && <div className="text-[0.72em] italic text-gray-400">Start a conversation...</div>}
          {messages.map((m: ChatMessage) => (
            <div key={m.id} className="space-y-[0.25em]">
              {/* A client-tool turn can carry NO text at all: the model's whole
                  output for that turn was the `tool_use` block (e.g. it emitted
                  `navigate_to` and said nothing). Rendering the bubble anyway
                  produced an empty grey box above the usage footer — it reads as
                  a broken/blank reply. The turn is not contentless though: the
                  footer below still reports the tokens and
                  `(stopped: awaiting_client_tool)`, and the continuation's own
                  bubble follows once the browser reports back. This also covers
                  session resume, where the same turn is persisted with
                  `content: ''`. */}
              {(m.content.trim() !== '' ||
                m.image ||
                (m.toolCalls && m.toolCalls.length > 0) ||
                Boolean(m.attachments?.length) ||
                Boolean(m.attachedFiles?.length)) && (
                <div
                  className={
                    m.role === 'user'
                      ? 'rounded-md bg-blue-100 px-[0.75em] py-[0.5em] text-gray-800 dark:bg-blue-900/40 dark:text-gray-100'
                      : 'rounded-md bg-gray-50 px-[0.75em] py-[0.5em] text-gray-800 dark:bg-gray-800 dark:text-gray-100'
                  }
                  style={{ ...(m.role === 'user' ? userBubbleStyle : assistantBubbleStyle), fontSize: '0.875em' }}
                >
                  <ChatbotMessage content={m.content} toolCalls={m.toolCalls} />
                  {m.image && (
                    <img
                      src={m.image}
                      alt="Captured still"
                      className="mt-[0.5em] max-h-64 w-auto rounded-md border border-black/10 dark:border-white/10"
                    />
                  )}
                  <MessageAttachments message={m} />

                </div>
              )}
              {!anon && (m.usage || m.earlyTerminationReason) && (
                <div className="text-[0.62em] text-gray-400">
                  {m.usage && (
                    <>
                      {m.usage.model}: {m.usage.inputTokens} in · {m.usage.outputTokens} out · $
                      {m.usage.cost.toFixed(4)}
                    </>
                  )}
                  {typeof m.continuationRounds === 'number' && m.continuationRounds > 1 && (
                    <> · {m.continuationRounds} rounds</>
                  )}
                  {m.earlyTerminationReason && <> (stopped: {humanizeTerminationReason(m.earlyTerminationReason)})</>}
                </div>
              )}
            </div>
          ))}
          {streaming && (
            <div className="space-y-[0.25em]">
              {currentToolCalls.length > 0 && (
                <div className="flex flex-wrap gap-[0.25em]">
                  {currentToolCalls.map((p: ToolPillState) => (
                    <ChatbotToolPill
                      key={p.toolCallId}
                      name={p.name}
                      status={p.status}
                      resultSummary={p.resultSummary}
                      durationMs={p.durationMs}
                    />
                  ))}
                </div>
              )}
              <div
                className="rounded-md bg-gray-50 px-[0.75em] py-[0.5em] text-gray-800 dark:bg-gray-800 dark:text-gray-100"
                style={{ ...assistantBubbleStyle, fontSize: '0.875em' }}
              >
                <ChatbotMessage content={currentResponse || '...'} />
                <span className="inline-block h-3 w-1.5 animate-pulse bg-gray-500 dark:bg-gray-300" aria-hidden />
              </div>
            </div>
          )}
          {sending && !streaming && (
            <div
              className="rounded-md bg-gray-50 px-[0.75em] py-[0.5em] text-[0.72em] text-gray-500 dark:bg-gray-800 dark:text-gray-400"
              style={assistantBubbleStyle}
            >
              Thinking...
            </div>
          )}
          {error && typeof error === 'string' && (
            <div className="rounded-md bg-red-50 px-[0.75em] py-[0.5em] text-[0.72em] text-red-700 dark:bg-red-900/30 dark:text-red-300">
              Error: {error}
            </div>
          )}
        </div>
        {showJump && (
          <button
            type="button"
            onClick={jumpToLatest}
            className="absolute bottom-[0.5em] left-1/2 -translate-x-1/2 rounded-full border border-gray-200 bg-white/95 px-[0.75em] py-[0.25em] text-[0.68em] font-medium text-gray-600 shadow-md hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800/95 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            ↓ Jump to latest
          </button>
        )}
      </div>
    )
  }),
)

function humanizeTerminationReason(reason: string): string {
  switch (reason) {
    case 'max_iterations':
      return 'max rounds reached'
    case 'repeated_tool_call':
      return 'repeated tool call'
    case 'no_text_progress':
      return 'no progress'
    case 'repeated_tool_error':
      return 'repeated tool error'
    case 'provider_error':
      return 'provider error'
    case 'user_cancelled':
      return 'cancelled by user'
    default:
      return reason
  }
}
